import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { db } from "../firebase";

function AdminEmployeeHistory() {
  const navigate =
    useNavigate();

  const { employeeId } =
    useParams();

  const [employee, setEmployee] =
    useState(null);

  const [logs, setLogs] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [message, setMessage] =
    useState("");

  const [fromDate, setFromDate] =
    useState("");

  const [toDate, setToDate] =
    useState("");

  useEffect(() => {
    const loadHistory =
      async () => {
        try {
          setLoading(true);
          setMessage("");

          const employeeSnapshot =
            await getDoc(
              doc(
                db,
                "employees",
                employeeId
              )
            );

          if (
            employeeSnapshot.exists()
          ) {
            setEmployee({
              id:
                employeeSnapshot.id,
              ...employeeSnapshot.data(),
            });
          }

          try {
            const historyQuery =
              query(
                collection(
                  db,
                  "attendanceLogs"
                ),
                where(
                  "employeeId",
                  "==",
                  employeeId
                )
              );

            const historySnapshot =
              await getDocs(
                historyQuery
              );

            const historyList =
              historySnapshot.docs.map(
                (item) => ({
                  id: item.id,
                  ...item.data(),
                })
              );

            setLogs(
              historyList
            );
          } catch (
            historyError
          ) {
            console.error(
              "Attendance history loading error:",
              historyError
            );

            setMessage(
              "Attendance history could not be loaded. Check Firestore permissions for attendanceLogs."
            );
          }
        } catch (error) {
          console.error(
            "Employee loading error:",
            error
          );

          setMessage(
            "Unable to load employee details."
          );
        } finally {
          setLoading(false);
        }
      };

    loadHistory();
  }, [employeeId]);

  const toDateObject = (
    value
  ) => {
    if (!value) {
      return null;
    }

    try {
      if (
        typeof value.toDate ===
        "function"
      ) {
        return value.toDate();
      }

      const date =
        new Date(value);

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return null;
      }

      return date;
    } catch (error) {
      return null;
    }
  };

  const formatTime = (
    value
  ) => {
    const date =
      toDateObject(value);

    if (!date) {
      return "-";
    }

    return date.toLocaleTimeString(
      "en-IN",
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  const formatDate = (
    dateKey
  ) => {
    if (
      !dateKey ||
      !/^\d{4}-\d{2}-\d{2}$/.test(
        dateKey
      )
    ) {
      return dateKey || "-";
    }

    const [
      year,
      month,
      day,
    ] = dateKey.split("-");

    return `${day}/${month}/${year}`;
  };

  const formatDuration = (
    secondsValue
  ) => {
    const totalSeconds =
      Math.max(
        0,
        Math.floor(
          Number(secondsValue) ||
            0
        )
      );

    const hours =
      Math.floor(
        totalSeconds / 3600
      );

    const minutes =
      Math.floor(
        (totalSeconds % 3600) /
          60
      );

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }

    return `${minutes}m`;
  };

  const filteredLogs =
    useMemo(() => {
      let list =
        [...logs];

      if (fromDate) {
        list =
          list.filter(
            (log) =>
              log.date >=
              fromDate
          );
      }

      if (toDate) {
        list =
          list.filter(
            (log) =>
              log.date <=
              toDate
          );
      }

      list.sort(
        (a, b) =>
          String(
            b.date || ""
          ).localeCompare(
            String(
              a.date || ""
            )
          )
      );

      return list;
    }, [
      logs,
      fromDate,
      toDate,
    ]);

  const getLocalDateKey = (
    value
  ) => {
    const date =
      value instanceof Date
        ? value
        : new Date(value);

    const year =
      date.getFullYear();

    const month =
      String(
        date.getMonth() + 1
      ).padStart(2, "0");

    const day =
      String(
        date.getDate()
      ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const setQuickDate = (
    type
  ) => {
    const now =
      new Date();

    if (
      type === "today"
    ) {
      const value =
        getLocalDateKey(
          now
        );

      setFromDate(value);
      setToDate(value);
      return;
    }

    if (
      type ===
      "yesterday"
    ) {
      const yesterday =
        new Date(now);

      yesterday.setDate(
        yesterday.getDate() -
          1
      );

      const value =
        getLocalDateKey(
          yesterday
        );

      setFromDate(value);
      setToDate(value);
      return;
    }

    if (
      type === "week"
    ) {
      const start =
        new Date(now);

      start.setDate(
        now.getDate() -
          now.getDay()
      );

      setFromDate(
        getLocalDateKey(
          start
        )
      );

      setToDate(
        getLocalDateKey(
          now
        )
      );

      return;
    }

    if (
      type === "month"
    ) {
      const start =
        new Date(
          now.getFullYear(),
          now.getMonth(),
          1
        );

      setFromDate(
        getLocalDateKey(
          start
        )
      );

      setToDate(
        getLocalDateKey(
          now
        )
      );
    }
  };

  const downloadCsv =
    () => {
      const rows = [
        [
          "Employee",
          "Email",
          "Date",
          "Login",
          "Work End",
          "Break Count",
          "Break Details",
          "Total Break",
          "Effective Work",
          "Status",
        ],

        ...filteredLogs.map(
          (log) => {
            const breaks =
              Array.isArray(
                log.breaks
              )
                ? log.breaks
                : [];

            const breakDetails =
              breaks
                .map(
                  (
                    breakItem,
                    index
                  ) =>
                    `Break ${
                      index + 1
                    }: ${formatTime(
                      breakItem.startedAt
                    )} - ${formatTime(
                      breakItem.endedAt
                    )} (${formatDuration(
                      breakItem.durationSeconds
                    )})`
                )
                .join(" | ");

            return [
              employee?.name ||
                "",
              employee?.email ||
                "",
              formatDate(
                log.date
              ),
              formatTime(
                log.workStartedAt
              ),
              formatTime(
                log.workEndedAt
              ),
              Number(
                log.breakCount
              ) || 0,
              breakDetails ||
                "No Break",
              formatDuration(
                log.totalBreakSeconds
              ),
              formatDuration(
                log.effectiveWorkSeconds
              ),
              log.status ||
                "",
            ];
          }
        ),
      ];

      const csv =
        rows
          .map(
            (row) =>
              row
                .map(
                  (value) =>
                    `"${String(
                      value ?? ""
                    ).replace(
                      /"/g,
                      '""'
                    )}"`
                )
                .join(",")
          )
          .join("\n");

      const blob =
        new Blob(
          [csv],
          {
            type:
              "text/csv;charset=utf-8;",
          }
        );

      const url =
        URL.createObjectURL(
          blob
        );

      const link =
        document.createElement(
          "a"
        );

      link.href = url;

      link.download =
        `${employee?.name || "employee"}-attendance-history.csv`;

      document.body.appendChild(
        link
      );

      link.click();

      document.body.removeChild(
        link
      );

      URL.revokeObjectURL(
        url
      );
    };

  return (
    <div className="admin-dashboard-page">
      <header className="dashboard-header">
        <div>
          <h1>
            Trading Wealth CRM
          </h1>

          <p>
            Employee Attendance History
          </p>
        </div>

        <button
          type="button"
          className="logout-button"
          onClick={() =>
            navigate(
              "/admin-reports?type=employees"
            )
          }
        >
          Back to Employees
        </button>
      </header>

      <main className="dashboard-main">
        <section className="dashboard-welcome">
          <p className="dashboard-label">
            EMPLOYEE HISTORY
          </p>

          <h2>
            {employee?.name ||
              "Employee"}
          </h2>

          <p>
            {employee?.email ||
              ""}
          </p>

          <p
            style={{
              marginTop: "8px",
            }}
          >
            Current Status:{" "}
            <strong>
              {employee?.workStatus ||
                "Not Started"}
            </strong>
          </p>
        </section>

        <section className="employee-form-card">
          <div className="employee-form-grid">
            <div className="form-group">
              <label>
                From Date
              </label>

              <input
                type="date"
                value={
                  fromDate
                }
                onChange={(
                  event
                ) =>
                  setFromDate(
                    event.target.value
                  )
                }
              />
            </div>

            <div className="form-group">
              <label>
                To Date
              </label>

              <input
                type="date"
                value={
                  toDate
                }
                onChange={(
                  event
                ) =>
                  setToDate(
                    event.target.value
                  )
                }
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              marginTop: "15px",
            }}
          >
            <button
              type="button"
              className="table-button"
              onClick={() =>
                setQuickDate(
                  "today"
                )
              }
            >
              Today
            </button>

            <button
              type="button"
              className="table-button"
              onClick={() =>
                setQuickDate(
                  "yesterday"
                )
              }
            >
              Yesterday
            </button>

            <button
              type="button"
              className="table-button"
              onClick={() =>
                setQuickDate(
                  "week"
                )
              }
            >
              This Week
            </button>

            <button
              type="button"
              className="table-button"
              onClick={() =>
                setQuickDate(
                  "month"
                )
              }
            >
              This Month
            </button>

            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                setFromDate("");
                setToDate("");
              }}
            >
              Complete History
            </button>

            <button
              type="button"
              className="admin-login-button employee-save-button"
              onClick={
                downloadCsv
              }
            >
              Download CSV
            </button>
          </div>
        </section>

        {message && (
          <div className="attendance-message">
            {message}
          </div>
        )}

        {loading ? (
          <div className="employee-empty-state">
            Loading attendance history...
          </div>
        ) : (
          <section className="dashboard-section">
            <div className="section-heading">
              <div>
                <p className="dashboard-label">
                  COMPLETE LOGIN & BREAK HISTORY
                </p>

                <h2>
                  {
                    filteredLogs.length
                  }{" "}
                  Working Days
                </h2>
              </div>
            </div>

            {filteredLogs.length ===
            0 ? (
              <div className="employee-empty-state">
                No saved attendance history found yet.
              </div>
            ) : (
              <div>
                {filteredLogs.map(
                  (log) => {
                    const breaks =
                      Array.isArray(
                        log.breaks
                      )
                        ? log.breaks
                        : [];

                    return (
                      <div
                        key={
                          log.id
                        }
                        className="employee-form-card"
                        style={{
                          marginBottom:
                            "16px",
                        }}
                      >
                        <div
                          style={{
                            display:
                              "flex",
                            justifyContent:
                              "space-between",
                            gap:
                              "10px",
                            flexWrap:
                              "wrap",
                          }}
                        >
                          <div>
                            <p className="dashboard-label">
                              DATE
                            </p>

                            <h2>
                              {formatDate(
                                log.date
                              )}
                            </h2>
                          </div>

                          <div>
                            <strong>
                              {log.status ||
                                "-"}
                            </strong>
                          </div>
                        </div>

                        <div
                          className="attendance-time-row"
                          style={{
                            marginTop:
                              "16px",
                          }}
                        >
                          <div>
                            <span>
                              Login
                            </span>

                            <strong>
                              {formatTime(
                                log.workStartedAt
                              )}
                            </strong>
                          </div>

                          <div>
                            <span>
                              Work End
                            </span>

                            <strong>
                              {formatTime(
                                log.workEndedAt
                              )}
                            </strong>
                          </div>

                          <div>
                            <span>
                              Break Count
                            </span>

                            <strong>
                              {breaks.length}
                            </strong>
                          </div>

                          <div>
                            <span>
                              Total Break
                            </span>

                            <strong>
                              {formatDuration(
                                log.totalBreakSeconds
                              )}
                            </strong>
                          </div>

                          <div>
                            <span>
                              Effective Work
                            </span>

                            <strong>
                              {formatDuration(
                                log.effectiveWorkSeconds
                              )}
                            </strong>
                          </div>
                        </div>

                        <div
                          style={{
                            marginTop:
                              "20px",
                          }}
                        >
                          <h3>
                            Break Details
                          </h3>

                          {breaks.length ===
                          0 ? (
                            <p
                              style={{
                                color:
                                  "#64748b",
                                marginTop:
                                  "8px",
                              }}
                            >
                              No break used.
                            </p>
                          ) : (
                            breaks.map(
                              (
                                breakItem,
                                index
                              ) => (
                                <div
                                  key={
                                    `${log.id}-break-${index}`
                                  }
                                  style={{
                                    padding:
                                      "10px 12px",
                                    marginTop:
                                      "8px",
                                    border:
                                      "1px solid #bae6fd",
                                    borderRadius:
                                      "10px",
                                    background:
                                      "#f8fdff",
                                  }}
                                >
                                  <strong>
                                    Break{" "}
                                    {index +
                                      1}
                                  </strong>

                                  <div
                                    style={{
                                      marginTop:
                                        "5px",
                                    }}
                                  >
                                    {formatTime(
                                      breakItem.startedAt
                                    )}{" "}
                                    →{" "}
                                    {formatTime(
                                      breakItem.endedAt
                                    )}
                                  </div>

                                  <small>
                                    Duration:{" "}
                                    {formatDuration(
                                      breakItem.durationSeconds
                                    )}
                                  </small>
                                </div>
                              )
                            )
                          )}

                          {log.activeBreakStartedAt && (
                            <div
                              style={{
                                padding:
                                  "10px 12px",
                                marginTop:
                                  "8px",
                                border:
                                  "1px solid #facc15",
                                borderRadius:
                                  "10px",
                              }}
                            >
                              <strong>
                                Current Break
                              </strong>

                              <div>
                                Started:{" "}
                                {formatTime(
                                  log.activeBreakStartedAt
                                )}
                              </div>

                              <small>
                                Ongoing
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

export default AdminEmployeeHistory;